import { FunctionComponent, useRef, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ReactComponent as PersonIcon } from '../../asset/images/icons/person.svg';
import { ReactComponent as QuestionIcon } from '../../asset/images/icons/question_mark.svg';
import { ReactComponent as RocketIcon } from '../../asset/images/icons/rocket.svg';
import { ReactComponent as ProfileIcon } from '../../asset/images/icons/profile.svg';

import { ReactComponent as InfoIcon } from '../../asset/images/icons/info.svg';

import { ReactComponent as Ill1 } from '../../asset/images/onboarding_how_astronomic_works/new_var_1/ill_1.svg';
import { ReactComponent as Ill2 } from '../../asset/images/onboarding_how_astronomic_works/new_var_1/ill_2.svg';
import { ReactComponent as Ill3 } from '../../asset/images/onboarding_how_astronomic_works/new_var_1/ill_3.svg';
import { ReactComponent as Ill4 } from '../../asset/images/onboarding_how_astronomic_works/new_var_1/ill_4.svg';
import { ReactComponent as Ill5 } from '../../asset/images/onboarding_how_astronomic_works/new_var_1/ill_5.svg';
import { ReactComponent as Ill6 } from '../../asset/images/onboarding_how_astronomic_works/new_var_1/ill_6.svg';
import { ReactComponent as Ill7 } from '../../asset/images/onboarding_how_astronomic_works/new_var_1/ill_7.svg';
import { ReactComponent as Ill8 } from '../../asset/images/onboarding_how_astronomic_works/new_var_1/ill_8.svg';

import OnboardingTemplate from '../../containers/templates/OnboardingTemplate/OnboardingTemplate';
import Sidebar from '../../containers/templates/OnboardingTemplate/_parts/Sidebar/Sidebar';
import MetaTags from '../../components/seo/MetaTags/MetaTags';
import MainButton from '../../components/buttons/MainButton/MainButton';
import About from './_parts/About/About';
import styles from './OnboardingAboutView.module.scss';
import TiledList from './_parts/TiledList/TiledList';
import PopUp from '../../components/popups/PopUp/PopUp';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import RoutesEnum from '../../enums/routes';
import NavigationLink from '../../components/links/NavigationLink/NavigationLink';
import useMediaQuery from '../../hooks/useMediaQuery';

const stepKeys = [
  RoutesEnum.ONBOARDING_STEP_1,
  RoutesEnum.ONBOARDING_STEP_2,
  RoutesEnum.ONBOARDING_STEP_3,
  RoutesEnum.ONBOARDING_STEP_4,
];
const DEFAULT_STEP = RoutesEnum.ONBOARDING_STEP_1;

type OnboardingStepKey = typeof stepKeys[number];

function isValidStep(step: string): step is OnboardingStepKey {
  return stepKeys.includes(step as OnboardingStepKey);
}

const OnboardingAboutView: FunctionComponent = () => {
  const { step = DEFAULT_STEP } = useParams();
  const navigate = useNavigate();
  const aboutRef = useRef<{ handleOnSubmit: () => void }>(null);
  const [equityPopUpActive, setEquityPopUpActive] = useState(false);
  const [firstStepCompleted, setFirstStepCompleted] = useState(false);
  const isMobile = useMediaQuery('(max-width: 767px)');

  const resolvedStep = useMemo<OnboardingStepKey>(() => {
    return isValidStep(step) ? step : DEFAULT_STEP;
  }, [step]);

  const resolveFooterButtonSize = () => {
    return isMobile ? 'large' : 'medium';
  };

  const renderBackButtonContent = () => {
    return isMobile ? (
      <svg width="16" height="17" viewBox="0 0 16 17" fill="#E24E29">
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M8.70711 0.709641C9.09763 1.10017 9.09763 1.73333 8.70711 2.12385L3.41421 7.41675H15C15.5523 7.41675 16 7.86446 16 8.41675C16 8.96903 15.5523 9.41675 15 9.41675H3.41421L8.70711 14.7096C9.09763 15.1002 9.09763 15.7333 8.70711 16.1239C8.31658 16.5144 7.68342 16.5144 7.29289 16.1239L0.292893 9.12385C-0.0976311 8.73333 -0.0976311 8.10017 0.292893 7.70964L7.29289 0.709641C7.68342 0.319117 8.31658 0.319117 8.70711 0.709641Z"
        />
      </svg>
    ) : (
      'Back'
    );
  };

  const stepConfig: Partial<
    Record<
      OnboardingStepKey,
      {
        title: string;
        icon: JSX.Element;
        content: JSX.Element;
        footer: JSX.Element;
      }
    >
  > = {
    [stepKeys[0]]: {
      title: 'Tell us about yourself',
      icon: <PersonIcon />,
      content: <About ref={aboutRef} />,
      footer: (
        <>
          {/* we need this empty div below to properly handle css markup */}
          {isMobile || <div></div>}
          <MainButton
            key={0}
            sizeType={resolveFooterButtonSize()}
            onClick={async () => {
              const isSuccessful = await aboutRef.current?.handleOnSubmit();

              if (isSuccessful) {
                navigate(`/${RoutesEnum.ONBOARDING}/${RoutesEnum.ONBOARDING_STEP_2}`);
                setFirstStepCompleted(true);
              }
            }}
          >
            Continue
          </MainButton>
        </>
      ),
    },
    [stepKeys[1]]: {
      title: 'How Astronomic works',
      icon: <QuestionIcon />,
      content: (
        <TiledList
          title="How Astronomic works"
          list={[
            {
              title: 'Find products and services on our site',
              text: 'What you need, when you need it.',
              illustration: <Ill1 className={styles['ill-1']} />,
            },
            {
              title: 'Speak with a Solutions Specialist',
              text: 'For estimates and answers.',
              illustration: <Ill2 className={styles['ill-2']} />,
            },
            {
              title: 'Pay with cards, ACH, or Rocket Fuel',
              text: 'Earn rewards with every purchase.',
              illustration: <Ill3 className={styles['ill-3']} />,
            },
          ]}
        />
      ),
      footer: (
        <>
          {/* we need this empty div below to properly handle css markup */}
          {isMobile || <div></div>}

          <MainButton
            key={2}
            sizeType={resolveFooterButtonSize()}
            onClick={() => navigate(`/${RoutesEnum.ONBOARDING}/${RoutesEnum.ONBOARDING_STEP_3}`)}
          >
            Continue
          </MainButton>
        </>
      ),
    },
    [stepKeys[2]]: {
      title: 'Rocket Fuel Credits',
      icon: <RocketIcon />,
      content: (
        <TiledList
          title="Rocket Fuel credits"
          list={[
            {
              title: 'Earn $5 in Rocket Fuel for every $100 you spend',
              text: 'For use with any of our products or services.',
              illustration: <Ill4 className={styles['ill-4']} />,
            },
            {
              title: 'Save more by prepaying for Rocket Fuel',
              text: 'One-time or monthly.',
              illustration: <Ill5 className={styles['ill-5']} />,
            },
            {
              title: 'Grant equity for discounts',
              text: (
                <>
                  Maximize your savings. Grant 1-3% equity to Astronomic and receive up to 36% site-wide discounts.{' '}
                  <NavigationLink path="" onClick={() => setEquityPopUpActive(true)} className="text text--bold">
                    How does this work?
                  </NavigationLink>
                </>
              ),
              illustration: <Ill6 className={styles['ill-6']} />,
            },
          ]}
        />
      ),
      footer: (
        <>
          <MainButton
            key={3}
            sizeType={resolveFooterButtonSize()}
            visualType="white"
            onClick={() => navigate(`/${RoutesEnum.ONBOARDING}/${RoutesEnum.ONBOARDING_STEP_2}`)}
          >
            {renderBackButtonContent()}
          </MainButton>
          <MainButton
            key={4}
            sizeType={resolveFooterButtonSize()}
            onClick={() => navigate(`/${RoutesEnum.ONBOARDING}/${RoutesEnum.ONBOARDING_STEP_4}`)}
          >
            Continue
          </MainButton>
        </>
      ),
    },
    [stepKeys[3]]: {
      title: 'Your Profile',
      icon: <ProfileIcon />,
      content: (
        <TiledList
          title="Complete Your Profile"
          list={[
            {
              title: 'Why this is important?',
              text: 'Our goals are to serve your needs as relevantly, affordably, and efficiently as possible, as well as to connect you with other founders, mentors, investors, and builders with relevant experience. The more you’re comfortable sharing, the better-equipped we’ll be to make relevant connections for you.',
              illustration: <Ill7 className={styles['ill-7']} />,
            },
            {
              title: 'Earn free Rocket Fuel',
              text: 'Now that you understand how Rocket Fuel works, fill out your profile to earn as much as $250 in free Rocket Fuel credits. You can use these credits to buy any product or service on Astronomic. ',
              illustration: <Ill8 className={styles['ill-8']} />,
            },
          ]}
        />
      ),
      footer: (
        <>
          <MainButton
            key={5}
            sizeType={resolveFooterButtonSize()}
            visualType="white"
            onClick={() => navigate(`/${RoutesEnum.ONBOARDING}/${RoutesEnum.ONBOARDING_STEP_3}`)}
          >
            {renderBackButtonContent()}
          </MainButton>
          <MainButton
            key={6}
            sizeType={resolveFooterButtonSize()}
            onClick={() => navigate(`/${RoutesEnum.COMPLETE_PROFILE}/`)}
          >
            Complete your profile
          </MainButton>
        </>
      ),
    },
  };

  const currentStepIndex = stepKeys.findIndex((key) => key === resolvedStep);
  const sidebarItems = stepKeys.map((key, index) => {
    const isDisabled = !firstStepCompleted || index === 0;

    return {
      title: stepConfig[key]?.title,
      icon: stepConfig[key]?.icon,
      disabled: isDisabled,
    };
  });

  const handleSidebarItemClick = (index: number) => {
    navigate(`/${RoutesEnum.ONBOARDING}/${stepKeys[index]}`);
  };

  return (
    <>
      <MetaTags title={`Onboarding - ${stepConfig[resolvedStep]?.title}`} />

      <OnboardingTemplate
        sidebar={
          <Sidebar list={sidebarItems as any} activeItem={currentStepIndex} onItemClick={handleSidebarItemClick} />
        }
        footer={<div className={styles['footer']}>{stepConfig[resolvedStep]?.footer}</div>}
      >
        {stepConfig[resolvedStep]?.content}
      </OnboardingTemplate>

      {createPortal(
        <PopUp className={styles['equity-pop-up']} active={equityPopUpActive} onPopUpStateChange={setEquityPopUpActive}>
          <div className={styles['equity-pop-up__icon-container']}>
            <InfoIcon style={{ fill: 'var(--brand-orange-600)', width: '32px' }} />
          </div>

          <p className={classNames(styles['equity-pop-up__title'], 'text text--h4')}>How equity grants work</p>

          <p className={classNames(styles['equity-pop-up__text'], 'text text--body-2')}>
            At Astronomic, our mission is to enable as much entrepreneurship as possible. To this end, we’re willing to
            discount our pricing as close to cost as possible if it means enabling our customers’ success.
            <br />
            <br />
            However, we can’t justify the risk of foregoing margin without a tradeoff. With this in mind, if you share
            the reward with us, we’ll share the risk with you. Think of it like “cofounder as a service.”
            <br />
            <br />
            Regardless of how your company is valued, if you grant 1%, 2%, or 3% of your equity to Astronomic, we’ll
            provide steep discounts in pricing across all of our products and services.
            <br />
            <br />
            Refer to our{' '}
            <NavigationLink path={`/${RoutesEnum.TERMS_OF_USE}`} target="_blank">
              Terms of Service
            </NavigationLink>{' '}
            for further clarification. After you register you can schedule a call with one of our Solutions Specialists
            to ask questions or begin the process.
          </p>
        </PopUp>,
        document.getElementById('modal') as HTMLElement
      )}
    </>
  );
};

export default OnboardingAboutView;
